import { useEffect } from 'react';
import config from '../config/config';

type FbqFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: (...args: unknown[]) => void;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    basePixelInitialized?: boolean;
    fbq?: FbqFunction;
  }
}

const BASE_PIXEL_ID = '531287465887555';
const FACEBOOK_SCRIPT_ID = 'facebook-pixel-script';
const FACEBOOK_PIXEL_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

const ensureFacebookPixelScript = (): Promise<void> => new Promise((resolve) => {
  if (typeof document === 'undefined') {
    resolve();
    return;
  }

  const existingScript = document.getElementById(FACEBOOK_SCRIPT_ID);
  if (existingScript) {
    resolve();
    return;
  }

  const firstScript = document.getElementsByTagName('script')[0];
  const script = document.createElement('script');
  script.id = FACEBOOK_SCRIPT_ID;
  script.async = true;
  script.src = FACEBOOK_PIXEL_SRC;

  script.onload = () => {
    console.log('[useBasePixel] Facebook Pixel script loaded successfully');
    resolve();
  };

  script.onerror = () => {
    console.error('[useBasePixel] Failed to load Facebook Pixel script');
    resolve();
  };

  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
});

const ensureFbq = (): FbqFunction | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  if (window.fbq) {
    return window.fbq;
  }

  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  }) as FbqFunction;

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];

  window.fbq = fbq;

  return fbq;
};

// Create fbq stub AND init the base pixel at module level, before any React
// render or effect. This guarantees the base pixel `init` is queued before
// child-component effects (useEventPixel, trackViewContent, etc.), so when
// fbevents.js loads and processes the queue every pixel is already registered.
if (typeof window !== 'undefined') {
  const fbq = ensureFbq();
  if (config.production && fbq && !window.basePixelInitialized) {
    fbq('set', 'autoConfig', false, BASE_PIXEL_ID);
    fbq('init', BASE_PIXEL_ID);
    window.basePixelInitialized = true;
  }
}

/**
 * Hook that loads the Facebook Pixel script and fires the initial PageView.
 * The base pixel init already happened at module level (queued in the stub).
 */
const useBasePixel = () => {
  useEffect(() => {
    if (!config.production) {
      return;
    }

    const loadAndTrack = async () => {
      try {
        await ensureFacebookPixelScript();

        const { fbq } = window;
        if (fbq) {
          fbq('track', 'PageView');
        }
      } catch (error) {
        console.error('[useBasePixel] Error loading pixel script:', error);
      }
    };

    loadAndTrack();
  }, []);
};

export default useBasePixel;
